"use client";

import { motion } from "framer-motion";
import { Loader2, Mail, MapPin, Send } from "lucide-react";
import type { SubmitEvent } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { StatusDialog } from "@/components/ui/status-dialog";
import { Textarea } from "@/components/ui/textarea";
import { siteConfig } from "@/lib/seo/config";
import { useContactStore } from "@/lib/store/contact-store";

const contactInfo = [
	{
		icon: Mail,
		title: "Email",
		value: siteConfig.business.email,
		href: `mailto:${siteConfig.business.email}`,
	},
	{
		icon: MapPin,
		title: "Location",
		value: `${siteConfig.business.address.streetAddress}, ${siteConfig.business.address.addressLocality}, ${siteConfig.business.address.addressRegion} ${siteConfig.business.address.postalCode}`,
		href: `https://maps.google.com/?q=${encodeURIComponent(
			`${siteConfig.business.address.streetAddress}, ${siteConfig.business.address.addressLocality}, ${siteConfig.business.address.addressRegion} ${siteConfig.business.address.postalCode}`,
		)}`,
	},
];

const subjects = [
	"General Inquiry",
	"Order Question",
	"Product Feedback",
	"Shipping & Delivery",
	"Return or Refund",
	"Partnership Opportunity",
	"Other",
];

export function ContactForm() {
	const {
		formData,
		fieldErrors,
		isSubmitting,
		isSubmitted,
		error,
		setFormField,
		submitForm,
		resetForm,
	} = useContactStore();

	const handleSubmit = async (e: SubmitEvent<HTMLFormElement>) => {
		e.preventDefault();
		await submitForm();
	};

	return (
		<div className="min-h-screen bg-zinc-50 dark:bg-black transition-colors duration-500">
			<div className="container mx-auto px-4 py-24">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
					className="max-w-2xl mx-auto text-center mb-12"
				>
					<h1 className="text-5xl font-black tracking-tight mb-4">
						Get in Touch
					</h1>
					<p className="text-xl text-muted-foreground">
						Have questions about our meals or delivery? We're here to help!
					</p>
				</motion.div>

				{/* Contact info cards */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.1 }}
					className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12 max-w-2xl mx-auto"
				>
					{contactInfo.map((item, index) => (
						<motion.div
							key={item.title}
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
						>
							<div className="text-center hover:shadow-lg transition-shadow h-full bg-background border border-border rounded-2xl p-6">
								<div className="bg-primary/10 rounded-full p-4 w-fit mx-auto mb-4">
									<item.icon className="h-6 w-6 text-primary" />
								</div>
								<h3 className="font-semibold mb-2">{item.title}</h3>
								{item.href ? (
									<a
										href={item.href}
										className="text-sm text-muted-foreground hover:text-primary transition-colors"
									>
										{item.value}
									</a>
								) : (
									<p className="text-sm text-muted-foreground">{item.value}</p>
								)}
							</div>
						</motion.div>
					))}
				</motion.div>

				{/* Wide horizontal form */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5, delay: 0.3 }}
					className="max-w-5xl mx-auto"
				>
					<div className="bg-background border border-border rounded-3xl shadow-xl overflow-hidden">
						{/* Form header bar */}
						<div className="bg-primary px-8 py-5">
							<h2 className="text-xl font-bold text-primary-foreground">
								Send us a Message
							</h2>
							<p className="text-sm text-primary-foreground/80 mt-0.5">
								Fill in the form below and we&apos;ll get back to you within 48
								hours.
							</p>
						</div>

						<div className="p-8">
							<form onSubmit={handleSubmit}>
								{/* Honeypot anti-spam: invisible to humans, bots fill it in */}
								<div
									aria-hidden="true"
									className="pointer-events-none absolute `-left-2499.75 h-px w-px overflow-hidden"
								>
									<Label htmlFor="website">Website</Label>
									<Input
										id="website"
										name="website"
										type="text"
										value={formData.website}
										onChange={(e) => setFormField("website", e.target.value)}
										onFocus={(e) => e.currentTarget.removeAttribute("readonly")}
										tabIndex={-1}
										autoComplete="off"
										readOnly
									/>
								</div>
								{/* Two-column grid */}
								<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
									{/* Left column */}
									<div className="space-y-5">
										{/* Name */}
										<div className="space-y-2">
											<Label htmlFor="name">Your full name *</Label>
											<Input
												id="name"
												placeholder="Your full name"
												value={formData.name}
												onChange={(e) => setFormField("name", e.target.value)}
												disabled={isSubmitting}
												aria-invalid={!!fieldErrors.name}
												className={`rounded-xl h-11 ${fieldErrors.name ? "border-destructive" : ""}`}
												required
											/>
											{fieldErrors.name && (
												<p className="text-xs text-destructive">
													{fieldErrors.name}
												</p>
											)}
										</div>

										{/* Email */}
										<div className="space-y-2">
											<Label htmlFor="email">Email address *</Label>
											<Input
												id="email"
												type="email"
												placeholder="you@example.com"
												value={formData.email}
												onChange={(e) => setFormField("email", e.target.value)}
												disabled={isSubmitting}
												aria-invalid={!!fieldErrors.email}
												className={`rounded-xl h-11 ${fieldErrors.email ? "border-destructive" : ""}`}
												required
											/>
											{fieldErrors.email && (
												<p className="text-xs text-destructive">
													{fieldErrors.email}
												</p>
											)}
										</div>

										{/* Phone */}
										<div className="space-y-2">
											<Label htmlFor="phone">Phone number (optional)</Label>
											<Input
												id="phone"
												type="tel"
												placeholder="(555) 123-4567"
												value={formData.phone}
												onChange={(e) => setFormField("phone", e.target.value)}
												disabled={isSubmitting}
												aria-invalid={!!fieldErrors.phone}
												className={`rounded-xl h-11 ${fieldErrors.phone ? "border-destructive" : ""}`}
											/>
											{fieldErrors.phone && (
												<p className="text-xs text-destructive">
													{fieldErrors.phone}
												</p>
											)}
										</div>

										{/* Subject */}
										<div className="space-y-2">
											<Label htmlFor="subject">Select a topic *</Label>
											<Select
												value={formData.subject}
												onValueChange={(value) =>
													setFormField("subject", value)
												}
												disabled={isSubmitting}
												required
											>
												<SelectTrigger className="rounded-xl h-11">
													<SelectValue placeholder="Select a topic" />
												</SelectTrigger>
												<SelectContent className="rounded-xl">
													{subjects.map((subject) => (
														<SelectItem key={subject} value={subject}>
															{subject}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											{fieldErrors.subject && (
												<p className="text-xs text-destructive">
													{fieldErrors.subject}
												</p>
											)}
										</div>
									</div>

									{/* Right column */}
									<div className="flex flex-col gap-5">
										{/* Message */}
										<div className="space-y-2 flex-1 flex flex-col">
											<Label htmlFor="message">How can we help you? *</Label>
											<Textarea
												id="message"
												placeholder="How can we help you?"
												className={`flex-1 min-h-37.5 rounded-xl resize-none overflow-y-auto shadow-none custom-scrollbar ${fieldErrors.message ? "border-destructive" : ""}`}
												value={formData.message}
												onChange={(e) =>
													setFormField("message", e.target.value)
												}
												disabled={isSubmitting}
												aria-invalid={!!fieldErrors.message}
												required
											/>
											{fieldErrors.message && (
												<p className="text-xs text-destructive">
													{fieldErrors.message}
												</p>
											)}
										</div>

										{/* Newsletter */}
										<div className="flex items-center gap-2">
											<Checkbox
												id="newsletter"
												checked={formData.newsletter}
												onCheckedChange={(checked) =>
													setFormField("newsletter", checked === true)
												}
												disabled={isSubmitting}
											/>
											<Label
												htmlFor="newsletter"
												className="text-sm font-normal cursor-pointer"
											>
												Keep me updated with news and offers
											</Label>
										</div>

										{/* Submit */}
										<Button
											type="submit"
											className="w-full h-12 font-bold rounded-xl text-base"
											disabled={isSubmitting}
										>
											{isSubmitting ? (
												<>
													<Loader2 className="mr-2 h-4 w-4 animate-spin" />
													Sending...
												</>
											) : (
												<>
													<Send className="mr-2 h-4 w-4" />
													Send Message
												</>
											)}
										</Button>
									</div>
								</div>
							</form>
						</div>
					</div>
				</motion.div>
			</div>

			{/* Global State Handling */}
			<StatusDialog
				open={isSubmitting || !!error || isSubmitted}
				onOpenChange={(open) => {
					if (!open) {
						if (isSubmitted) resetForm();
						else if (error) useContactStore.setState({ error: null });
					}
				}}
				state={
					isSubmitting
						? "loading"
						: error
							? "error"
							: isSubmitted
								? "success"
								: "idle"
				}
				loadingTitle="Sending Message..."
				error={error}
				onSuccessAction={resetForm}
				successTitle="Message Sent!"
				successDescription="Thank you for reaching out. We'll get back to you within 48 hours."
				successButtonText="Send Another Message"
				onErrorAction={() => useContactStore.setState({ error: null })}
			/>
		</div>
	);
}
